import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import {
  Zap,
  TrendingUp,
  DollarSign,
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  Wallet,
  ExternalLink,
} from "lucide-react";
import { format, parseISO } from "date-fns";

interface Invoice {
  id: string;
  supplier_name: string;
  supplier_email: string;
  payment_amount: number;
  discounted_payment_amount: number | null;
  discount_amount: number | null;
  payment_due_date: string;
  status: string;
  created_at: string;
  invoice_file_url: string | null;
}

export default function SupplierPortal() {
  const [searchParams] = useSearchParams();
  const supplierEmail = searchParams.get("email");
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);

  const formatDate = (dateString: string) => {
    try {
      return format(parseISO(dateString), "MMM d, yyyy");
    } catch {
      return dateString;
    }
  };

  useEffect(() => {
    const fetchInvoices = async () => {
      if (!supplierEmail) {
        setIsLoading(false);
        return;
      }
      const { data, error } = await supabase
        .from("invoices")
        .select("*")
        .eq("supplier_email", supplierEmail)
        .order("created_at", { ascending: false });

      if (!error && data) {
        setInvoices(data as Invoice[]);
      }
      setIsLoading(false);
    };
    fetchInvoices();
  }, [supplierEmail]);

  const accepted = invoices.filter(
    (i) => i.status === "early_payment_scheduled" || i.status === "early_payment_completed"
  );
  const pending = invoices.filter((i) => i.status === "pending");
  // const declined = invoices.filter((i) => i.status === "standard_payment");

  const totalAccelerated = accepted.reduce(
    (s, i) => s + (i.discounted_payment_amount || i.payment_amount),
    0
  );
  const totalSaved = accepted.reduce((s, i) => s + (i.discount_amount || 0), 0);
  const totalFaceValue = invoices.reduce((s, i) => s + i.payment_amount, 0);

  if (!supplierEmail) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-muted flex items-center justify-center p-4">
        <Card className="p-12 text-center max-w-md">
          <Zap className="w-12 h-12 text-primary mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Supplier Portal</h1>
          <p className="text-muted-foreground">
            Access your portal via the link provided in your early payment offer email.
          </p>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  const supplierName = invoices[0]?.supplier_name || supplierEmail;

  const statusConfig: Record<string, { label: string; icon: any; color: string }> = {
    pending: {
      label: "Pending",
      icon: Clock,
      color: "bg-warning/10 text-warning border-warning/20",
    },
    early_payment_scheduled: {
      label: "Scheduled",
      icon: CheckCircle,
      color: "bg-success/10 text-success border-success/20",
    },
    early_payment_completed: {
      label: "Completed",
      icon: CheckCircle,
      color: "bg-primary/10 text-primary border-primary/20",
    },
    standard_payment: {
      label: "Declined",
      icon: XCircle,
      color: "bg-muted text-muted-foreground border-border",
    },
    early_payment: {
      label: "Scheduled",
      icon: CheckCircle,
      color: "bg-success/10 text-success border-success/20",
    },
    paid: {
      label: "Completed",
      icon: CheckCircle,
      color: "bg-primary/10 text-primary border-primary/20",
    },
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card/50 backdrop-blur-sm">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl gradient-primary flex items-center justify-center shadow-primary">
              <Zap className="w-5 h-5 text-primary-foreground" />
            </div>
            <div>
              <h1 className="text-xl font-display font-bold text-foreground">
                DiscoRun AI — Supplier Portal
              </h1>
              <p className="text-sm text-muted-foreground">Welcome, {supplierName}</p>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8 max-w-4xl space-y-6">
        {/* Key Metrics */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card className="glass-card p-4">
            <div className="flex items-center gap-2 mb-2">
              <Wallet className="w-4 h-4 text-primary" />
              <span className="text-xs text-muted-foreground">Working Capital Accelerated</span>
            </div>
            <p className="text-xl font-bold text-foreground">{formatCurrency(totalAccelerated)}</p>
          </Card>
          <Card className="glass-card p-4">
            <div className="flex items-center gap-2 mb-2">
              <DollarSign className="w-4 h-4 text-success" />
              <span className="text-xs text-muted-foreground">Discount Paid</span>
            </div>
            <p className="text-xl font-bold text-foreground">{formatCurrency(totalSaved)}</p>
          </Card>
          <Card className="glass-card p-4">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-4 h-4 text-primary" />
              <span className="text-xs text-muted-foreground">Total Invoices</span>
            </div>
            <p className="text-xl font-bold text-foreground">{formatCurrency(totalFaceValue)}</p>
          </Card>
          <Card className="glass-card p-4">
            <div className="flex items-center gap-2 mb-2">
              <FileText className="w-4 h-4 text-primary" />
              <span className="text-xs text-muted-foreground">Offers Received</span>
            </div>
            <p className="text-xl font-bold text-foreground">{invoices.length}</p>
          </Card>
        </div>

        {/* Pending Offers */}
        {pending.length > 0 && (
          <Card className="glass-card overflow-hidden">
            <div className="p-6 border-b border-border">
              <h2 className="text-lg font-display font-semibold text-foreground">Pending Offers</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Review and respond to early payment offers
              </p>
            </div>
            <div className="divide-y divide-border">
              {pending.map((inv) => (
                <div key={inv.id} className="p-4 flex items-center gap-4">
                  <FileText className="w-5 h-5 text-warning flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-foreground">
                      {formatCurrency(inv.payment_amount)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Due: {formatDate(inv.payment_due_date)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-success">
                      {formatCurrency(inv.discounted_payment_amount || 0)}
                    </p>
                    <p className="text-xs text-muted-foreground">Early payment</p>
                  </div>
                  <Button size="sm" asChild>
                    <a href={`/offer/${inv.id}`}>Review</a>
                  </Button>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* All Invoices */}
        <Card className="glass-card overflow-hidden">
          <div className="p-6 border-b border-border">
            <h2 className="text-lg font-display font-semibold text-foreground">Invoice History</h2>
            <p className="text-sm text-muted-foreground mt-1">{invoices.length} total invoices</p>
          </div>
          <div className="divide-y divide-border">
            {invoices.map((inv) => {
              const sc = statusConfig[inv.status] || statusConfig.pending;
              const StatusIcon = sc.icon;
              return (
                <div key={inv.id} className="p-4 flex items-center gap-4">
                  <FileText className="w-5 h-5 text-primary flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-foreground">
                      {formatCurrency(inv.payment_amount)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formatDate(inv.created_at)} • Due: {formatDate(inv.payment_due_date)}
                    </p>
                  </div>
                  <Badge className={`${sc.color} border`}>
                    <StatusIcon className="w-3 h-3 mr-1" />
                    {sc.label}
                  </Badge>
                  <div className="text-right flex-shrink-0">
                    {inv.discount_amount && inv.status !== "standard_payment" ? (
                      <p className="text-sm text-success">-{formatCurrency(inv.discount_amount)}</p>
                    ) : null}
                  </div>
                  {inv.invoice_file_url && (
                    <a href={inv.invoice_file_url} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="w-4 h-4 text-muted-foreground hover:text-primary" />
                    </a>
                  )}
                </div>
              );
            })}
          </div>
        </Card>

        {/* CTA */}
        <Card className="glass-card p-6 text-center">
          <Zap className="w-8 h-8 text-primary mx-auto mb-3" />
          <h3 className="font-display font-semibold text-foreground mb-2">
            Set Up Your DiscoRun Profile
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            Create a profile to automatically accept early payment offers, set your preferences, and
            get paid faster.
          </p>
          <Button>Get Started with DiscoRun</Button>
        </Card>
      </main>
    </div>
  );
}
